import { useCallback, useEffect, useRef, useState, type MouseEvent, type Ref } from 'react';
import { type ColumnDef, type Row, type Table as TanTable } from '@tanstack/react-table';

import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Event tags
import { drawableEventTags } from '@/react/constants/eventTags';

// Shared components
import { MoreVerticalIcon, EditIcon } from '@components/icons';
import { ActivityLogIcon } from '@components/icons/ActivityLogIcon';
import { PopOutCell } from '@components/DataTable/PopOutCell';
import { CHECKBOX_CLASSES, ROW_ACTIONS_MENU_PROPS } from '@components/DataTable/constants';

import { useTranslation } from '@hooks/useTranslation';

import { cn } from '@/lib/utils';
import { formatTime } from '@/react/utils/formatters';
import { EVENT_CHIP_CLASS, tagChipColours } from '@components/common/eventChipStyles';
import { isPlaceholderPostalCode } from '@/react/utils/postalCode';
import { buildEventEditPath, currentLocationPath } from '@/react/utils/eventEditNavigation';
import { eventHistoryPath } from '@/react/utils/historyPaths';
import { playTickSound } from '../../../../utils/sounds';
import { EVENT_COLUMN_WIDTHS } from '@components/DataTable/columnWidths';
import { can, CAPABILITIES, type CurrentUser } from '@/react/lib/capabilities';

import { EventStatusBadge } from './eventStatusBadge';

import {
  EventOrderGenerateMenuItems,
  MENU_ITEM_CLASS,
} from '../../sections/EventsToolbar/EventOrderGenerateMenuItems';
import { formatEventDeliveryLabel } from '../../deliveryLabel';

import type { EventListItem } from '../../types';
import { UNDERLINE_SLIDE_IN } from '@/components/ui/link-underline';

function SelectAllHeader({ table }: { table: TanTable<EventListItem> }) {
  const allSelected = table.getIsAllPageRowsSelected();
  return (
    <Checkbox
      checked={allSelected || (table.getIsSomePageRowsSelected() && 'indeterminate')}
      onCheckedChange={(value) => {
        table.toggleAllPageRowsSelected(!!value);
        playTickSound(!!value);
      }}
      aria-label="Select all"
      className={CHECKBOX_CLASSES}
    />
  );
}

function SelectRowCell({ row }: { row: Row<EventListItem> }) {
  const selected = row.getIsSelected();
  return (
    <Checkbox
      checked={selected}
      onCheckedChange={(value) => {
        row.toggleSelected(!!value);
        playTickSound(!!value);
      }}
      aria-label="Select row"
      className={CHECKBOX_CLASSES}
    />
  );
}

function useEventDetailsOverflow() {
  const rowRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  const check = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const summary = summaryRef.current;
    setOverflowing(
      row.scrollWidth > row.clientWidth ||
        (summary != null && summary.scrollWidth > summary.clientWidth)
    );
  }, []);

  useEffect(check);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(row);
    return () => observer.disconnect();
  }, [check, overflowing]);

  return { rowRef, summaryRef, overflowing };
}

function EventDetailsSummary({
  event,
  expanded,
  rowRef,
  summaryRef,
}: {
  event: EventListItem;
  expanded?: boolean;
  rowRef?: Ref<HTMLDivElement>;
  summaryRef?: Ref<HTMLSpanElement>;
}) {
  const { t } = useTranslation();
  const parts = [event.eventType, `${event.guestCount} guests`];
  if (event.lineCount) parts.push(`${event.lineCount} line${event.lineCount > 1 ? 's' : ''}`);
  if (event.wayCount) parts.push(`${event.wayCount} way${event.wayCount > 1 ? 's' : ''}`);

  const chips = event.chips ?? [];
  const dateChips = chips.filter((chip) => chip.group === 'date');
  const requirementChips = chips.filter((chip) => chip.group === 'requirement');

  return (
    <div className="flex flex-col gap-2">
      <div ref={rowRef} className={cn('flex items-center gap-2', !expanded && 'overflow-hidden')}>
        {drawableEventTags(event.tags ?? []).map((tag) => (
          <span key={tag} className={cn(EVENT_CHIP_CLASS, tagChipColours(tag))}>
            {t(`event_tags.${tag}`, tag)}
          </span>
        ))}
        <span
          ref={summaryRef}
          className={cn(
            'min-w-0 font-inter text-nav leading-[1.5] text-off-black whitespace-nowrap',
            !expanded && 'overflow-hidden text-ellipsis'
          )}
        >
          {parts.join(' \u00b7 ')}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-inter text-xs leading-[1.25] text-off-black">
        <span>{event.eventDateFormatted}</span>
        <span>
          <span className="font-semibold">P:</span> {event.pickupTime ? formatTime(event.pickupTime) : '\u2014'}
        </span>
        <span>
          <span className="font-semibold">S:</span> {event.serviceTime ? formatTime(event.serviceTime) : '\u2014'}
        </span>
        {dateChips.map((chip) => (
          <span key={chip.key} className={cn(EVENT_CHIP_CLASS, chip.colours)}>
            {t(chip.labelKey, chip.labelParams)}
          </span>
        ))}
      </div>
      {requirementChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {requirementChips.map((chip) => (
            <span key={chip.key} className={cn(EVENT_CHIP_CLASS, chip.colours)}>
              {t(chip.labelKey, chip.labelParams)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function EventDetailsCell({ event }: { event: EventListItem }) {
  const { rowRef, summaryRef, overflowing } = useEventDetailsOverflow();
  const collapsed = <EventDetailsSummary event={event} rowRef={rowRef} summaryRef={summaryRef} />;
  if (!overflowing) return collapsed;

  return (
    <PopOutCell
      minWidth={EVENT_COLUMN_WIDTHS.eventDetails}
      collapsed={collapsed}
      expanded={<EventDetailsSummary event={event} expanded />}
    />
  );
}

function DeliveryDetails({ event, expanded }: { event: EventListItem; expanded?: boolean }) {
  const { t } = useTranslation();
  const contactParts = [event.contactName, event.contactPhone, event.organisationName].filter(Boolean);

  return (
    <div className="flex flex-col gap-1.5">
      <span className={cn(
        'font-inter text-sm leading-[1.5] font-semibold',
        isPlaceholderPostalCode(event.postalCode) ? 'text-chilli-red' : 'text-off-black',
        !expanded && 'whitespace-nowrap overflow-hidden text-ellipsis'
      )}>
        {formatEventDeliveryLabel(event, t)}
      </span>
      <span className={cn(
        'font-inter text-xs leading-[1.25] text-off-black',
        !expanded && 'whitespace-nowrap overflow-hidden text-ellipsis'
      )}>
        {contactParts.join(' \u00b7 ')}
      </span>
    </div>
  );
}

function formatSpecialist(sp: EventListItem['specialists'][number]): string {
  if (sp.label) return sp.label;
  if (sp.phone) return `${sp.name} \u00b7 ${sp.phone}`;
  return sp.name;
}

function SpecialistsList({ specialists }: { specialists: EventListItem['specialists'] }) {
  if (!specialists?.length) return <span className="text-grey">-</span>;

  return (
    <div className="flex flex-col gap-1.5">
      {specialists.map((sp, i) => (
        <span key={i} className="font-inter text-xs leading-[1.25] text-off-black whitespace-nowrap">
          {formatSpecialist(sp)}
        </span>
      ))}
    </div>
  );
}

function EventRowActions({
  event,
  onGenerateEvent,
  canGenerate,
}: {
  event: EventListItem;
  onGenerateEvent?: (eventId: number, documentTypes: string[]) => void;
  canGenerate: boolean;
}) {
  const { t } = useTranslation();
  const editEventPath =
    event.orderId != null
      ? buildEventEditPath(event.orderId, event.id, currentLocationPath())
      : null;
  const historyPath = event.orderId != null ? eventHistoryPath(event.orderId, event.id) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          tabIndex={-1}
          data-row-actions-trigger
          className="group/trigger flex items-center justify-center h-6 w-6 rounded-xs hover:bg-dinnerplate-white data-[state=open]:bg-dinnerplate-white cursor-pointer"
        >
          <MoreVerticalIcon className="w-1 h-3 text-grey group-hover/trigger:text-off-black group-data-[state=open]/trigger:text-off-black" />
          <span className="sr-only">{t('row_actions.open_menu')}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...ROW_ACTIONS_MENU_PROPS}
        className={cn(
          'w-[232px] bg-off-white border-[0.5px] border-dinnerplate-white rounded-lg',
          'px-0 py-3 shadow-[0px_0px_20px_0px_rgba(239,239,239,0.5)]'
        )}
      >
        <DropdownMenuItem
          asChild
          disabled={editEventPath == null}
          className={MENU_ITEM_CLASS}
        >
          <a href={editEventPath ?? undefined}>
            <EditIcon className="size-5" />
            {t('row_actions.edit')}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          disabled={historyPath == null}
          className={MENU_ITEM_CLASS}
        >
          <a href={historyPath ?? undefined}>
            <ActivityLogIcon className="size-5" />
            {t('row_actions.history')}
          </a>
        </DropdownMenuItem>
        <EventOrderGenerateMenuItems
          eventTypeKey={event.eventTypeKey}
          productLine={event.productLine}
          eventStatus={event.eventStatus}
          onGenerate={(documentTypes) => onGenerateEvent?.(Number(event.id), documentTypes)}
          canGenerate={canGenerate}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface EventsColumnsOptions {
  onEventIdClick?: (orderId: number) => void;
  onGenerateEvent?: (eventId: number, documentTypes: string[]) => void;
  /** Signed-in user — gates the row-level Generate menu (event_orders.generate). */
  currentUser?: CurrentUser;
}

export function getEventsColumns({ onEventIdClick, onGenerateEvent, currentUser }: EventsColumnsOptions = {}): ColumnDef<EventListItem>[] {
  const canGenerate = can(currentUser, CAPABILITIES.EVENT_ORDERS_GENERATE);

  return [
    {
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: ({ row }) => <SelectRowCell row={row} />,
      enableSorting: false,
      size: EVENT_COLUMN_WIDTHS.select,
    },
    {
      accessorKey: 'eventIdentifier',
      header: 'IDs',
      enableSorting: false,
      cell: ({ row }) => {
        const { eventIdentifier, salesOrderNumber, orderId } = row.original;
        const isClickable = onEventIdClick && orderId != null;
        return (
          <div className="flex flex-col gap-1.5">
            {isClickable ? (
              <button
                type="button"
                className={`${UNDERLINE_SLIDE_IN} self-start font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap`}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  onEventIdClick(orderId);
                }}
              >
                {eventIdentifier}
              </button>
            ) : (
              <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black">
                {eventIdentifier}
              </span>
            )}
            <span className="font-inter text-xs leading-[1.25] text-off-black">
              {salesOrderNumber}
            </span>
          </div>
        );
      },
      size: EVENT_COLUMN_WIDTHS.ids,
    },
    {
      id: 'eventDetails',
      header: 'Event details',
      enableSorting: false,
      cell: ({ row }) => <EventDetailsCell event={row.original} />,
      size: EVENT_COLUMN_WIDTHS.eventDetails,
    },
    {
      id: 'deliveryDetails',
      header: 'Delivery details',
      enableSorting: false,
      cell: ({ row }) => (
        <PopOutCell
          minWidth={EVENT_COLUMN_WIDTHS.deliveryDetails}
          collapsed={<DeliveryDetails event={row.original} />}
          expanded={<DeliveryDetails event={row.original} expanded />}
        />
      ),
      size: EVENT_COLUMN_WIDTHS.deliveryDetails,
    },
    {
      id: 'salesperson',
      header: 'Salesperson',
      enableSorting: false,
      cell: ({ row }) => {
        const sp = row.original.salesperson;
        const avatar = <Avatar size="lg" src={sp.avatarUrl ?? undefined} name={sp.name} />;
        const cellContent = (
          <div className="flex items-center gap-3">
            {avatar}
            <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap overflow-hidden text-ellipsis">
              {sp.name}
            </span>
          </div>
        );
        if (sp.name.length <= 14) return cellContent;
        return (
          <PopOutCell
            minWidth={EVENT_COLUMN_WIDTHS.salesperson}
            collapsed={cellContent}
            expanded={
              <div className="flex items-center gap-3">
                {avatar}
                <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap">
                  {sp.name}
                </span>
              </div>
            }
          />
        );
      },
      size: EVENT_COLUMN_WIDTHS.salesperson,
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => <EventStatusBadge status={row.original.eventStatus} />,
      size: EVENT_COLUMN_WIDTHS.status,
    },
    {
      id: 'specialists',
      header: 'Specialists',
      enableSorting: false,
      cell: ({ row }) => <SpecialistsList specialists={row.original.specialists} />,
      size: EVENT_COLUMN_WIDTHS.specialists,
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) => (
        <EventRowActions event={row.original} onGenerateEvent={onGenerateEvent} canGenerate={canGenerate} />
      ),
      enableSorting: false,
    },
  ];
}
